import {
  DndContext,
  DragOverlay,
  MouseSensor,
  TouchSensor,
  getClientRect,
  pointerWithin,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragMoveEvent,
  type DragStartEvent,
} from "@dnd-kit/core";
import type { Row } from "@tanstack/react-table";
import * as React from "react";

import { Box } from "../../components/ui/box";
import { Icon } from "../../components/ui/icon";
import { calculateSortOrder } from "../../lib/calculate-sort-order";
import {
  DND_DRAGGABLE_PROPS,
  DND_OVERLAY_PROPS,
  DND_TOUCH_ACTIVATION,
} from "../../lib/dnd-attrs";
import { createVelocityTracker, settleOverlay } from "../../lib/drop-settle";
import { cssEscape } from "../../lib/utils";
import type {
  DataTableRowDragConfig,
  DataTableRowDropResult,
} from "./data-table-types";

// ---------------------------------------------------------------------------
// Row drag — move a row to another group, or to a new slot in its own
// ---------------------------------------------------------------------------
//
// A grouped table's groups are the drop targets and its rows are the
// draggables. The drag source is a grip seated in the leading `paddingX`
// gutter of the selection lane, left of the checkbox, so the row's own
// press (open) and the checkbox's press (select) both keep their targets.
//
// The whole row lifts: `DragOverlay` carries a translucent `elevated` clone
// of the row's DOM that follows the pointer freely (translucent so the rows
// and the insertion line beneath it stay legible), the source row dims in
// place, and the group under the pointer wears the drop wash
// (`data-drop-over` on the group scope box: the band tints brand, the rows
// replay the hover wash). When the table places rows (`reorder`), an
// insertion line inside that group tracks the ghost's centre between the
// rows: the slot the drop will take. The slot's neighbours are read back
// through the group's row registry (every loaded row, rendered or not), and
// the drop reports them with a sort position midpointed between their sort
// values — the same grammar a board reports. A drop on the row's own slot
// is a no-op.
//
// The ghost is a DOM clone rather than a re-render because the row's cells
// are consumer renderers reading table context (selection, density, surface,
// group rows) that the overlay does not sit inside; the clone freezes each
// cell at its measured width so it needs none of the table's width vars.

/** Minimum grip hit box; the gutter grows it, never shrinks it. */
const GRIP_MIN_WIDTH = 16;

/** A group's rendered body rows. Each carries its index in the group. */
const ROW_SELECTOR = "tr[data-row-index]";

const ROW_MEASURING = { draggable: { measure: getClientRect } };

/**
 * What a grip carries as its draggable data. The host's only draggables are
 * grips and its only droppables are group scope boxes, so a drag event's
 * `active` is always a row and its `over`, when set, always a group.
 */
type RowDragData<TData> = {
  row: TData;
  rowId: string;
  groupKey: string;
  /** The `<tr>` the grip sits in — cloned into the overlay at lift. */
  getRowElement: () => HTMLElement | null;
};

type GroupDropData = {
  groupKey: string;
};

/** A frozen copy of the lifted row, sized to the row it came from. */
type RowGhost = {
  node: HTMLElement;
  width: number;
  height: number;
};

/** What a group hands the host so a slot can name its neighbours. */
type GroupRowsApi<TData> = {
  /** The loaded row at a group index, undefined for an unloaded slot. */
  getRowAt: (index: number) => Row<TData> | undefined;
  /** The group's full row count, loaded or not. */
  total: number;
};

/**
 * The slot a drop would take: the group, the index the row would occupy,
 * the rows either side (undefined at the group's edges), and where the line
 * paints inside the group scope box.
 */
type DropSlot<TData> = {
  groupKey: string;
  index: number;
  upper: Row<TData> | undefined;
  lower: Row<TData> | undefined;
  /** The slot sits past rows the group has not loaded: no lower neighbour. */
  atUnloadedTail: boolean;
  /** The dragged row's own place in its own group — dropping changes nothing. */
  own: boolean;
  /** Line offset from the group scope box's top edge (px). */
  lineY: number;
};

function rowDragId(groupKey: string, rowId: string): string {
  return `row:${groupKey}::${rowId}`;
}

function groupDropId(groupKey: string): string {
  return `group:${groupKey}`;
}

/**
 * Clones a row for the overlay. Every cell is pinned to its measured width,
 * the virtualizer's transform is dropped so the clone lays out at the
 * ghost's origin, and the hover-revealed grip is shown — it is what the
 * pointer holds. The clone sheds its row index so slot geometry never
 * counts it.
 */
function cloneRow(source: HTMLElement): RowGhost {
  const rect = source.getBoundingClientRect();
  const clone = source.cloneNode(true) as HTMLElement;
  const sourceCells = Array.from(source.children) as HTMLElement[];
  const cloneCells = Array.from(clone.children) as HTMLElement[];
  // A deep clone copies every child in order, so the two lists pair by index.
  sourceCells.forEach((sourceCell, index) => {
    const cell = cloneCells[index]!;
    const width = sourceCell.getBoundingClientRect().width;
    cell.style.width = `${width}px`;
    cell.style.minWidth = `${width}px`;
    cell.style.maxWidth = `${width}px`;
    cell.style.flex = "none";
  });
  clone.style.position = "relative";
  clone.style.top = "0";
  clone.style.left = "0";
  clone.style.transform = "none";
  clone.style.width = `${rect.width}px`;
  clone.style.height = `${rect.height}px`;
  clone.removeAttribute("data-row-index");
  clone.removeAttribute("data-row-id");
  clone.removeAttribute("data-row-click-stop");
  clone.querySelectorAll<HTMLElement>("[data-row-drag-source]").forEach((grip) => {
    grip.removeAttribute("data-row-drag-source");
    grip.style.opacity = "1";
  });
  return { node: clone, width: rect.width, height: rect.height };
}

/**
 * The overlay's content: a lifted `elevated` plate holding the row clone,
 * translucent so the slot it hovers stays readable through it.
 */
function RowGhostPlate({
  ghost,
  overlayRef,
}: {
  ghost: RowGhost;
  /** The plate node, read by the drop settle before the overlay unmounts. */
  overlayRef: React.RefObject<HTMLDivElement | null>;
}) {
  const ref = React.useRef<HTMLDivElement>(null);
  const setRef = React.useCallback(
    (node: HTMLDivElement | null) => {
      ref.current = node;
      overlayRef.current = node;
    },
    [overlayRef],
  );
  React.useLayoutEffect(() => {
    ref.current?.replaceChildren(ghost.node);
  }, [ghost]);
  return (
    <Box
      ref={setRef}
      surface="elevated"
      rounded="md"
      border
      width={ghost.width}
      height={ghost.height}
      overflow="hidden"
      cursor="grabbing"
      opacity={70}
      className="pointer-events-none"
      {...DND_OVERLAY_PROPS}
    />
  );
}

type ActiveRowDrag<TData> = RowDragData<TData> & {
  ghost: RowGhost | null;
  /** The row's index in its group at lift, when its `<tr>` was found. */
  index: number | null;
};

type RowDragContextValue<TData> = {
  canDrag: ((row: TData) => boolean) | undefined;
  canDrop: ((groupKey: string) => boolean) | undefined;
  /** Width of the leading gutter the grip rides (px). */
  gutterPx: number;
  active: ActiveRowDrag<TData> | null;
  /** Group scope boxes register their element: the slot's geometry frame. */
  registerScope: (groupKey: string, element: HTMLElement | null) => void;
  /** Groups register their loaded rows so a slot can name its neighbours. */
  registerRows: (groupKey: string, api: GroupRowsApi<TData> | null) => void;
};

// Generic context: consumers narrow through the typed hook.
const RowDragContext = React.createContext<RowDragContextValue<unknown> | null>(
  null,
);

function useRowDrag<TData>(): RowDragContextValue<TData> | null {
  // The context is typed on `unknown`; the provider below stored a value of
  // the table's own row type, and the table is the only reader.
  return React.useContext(RowDragContext) as RowDragContextValue<TData> | null;
}

/**
 * The live slot on its own context, so only the group scope boxes re-render
 * as the line moves — never the grip in every row.
 */
const DropSlotContext = React.createContext<DropSlot<unknown> | null>(null);

/** The group a row renders inside — read by its grip to name the drag source.
 *  The table mounts the provider around every group, grouped or not. */
const RowDragGroupKeyContext = React.createContext<string>("");

/**
 * Names the group its rows render inside and registers the group's loaded
 * rows with the host. `getRowAt` reads a row by its index in the group
 * (undefined while that slot is unloaded); `total` is the group's full count.
 */
export function DataTableRowDragGroupProvider<TData>({
  groupKey,
  getRowAt,
  total,
  children,
}: {
  groupKey: string;
  getRowAt: (index: number) => Row<TData> | undefined;
  total: number;
  children: React.ReactNode;
}) {
  const registerRows = useRowDrag<TData>()?.registerRows;
  React.useEffect(() => {
    if (!registerRows) return;
    registerRows(groupKey, { getRowAt, total });
    return () => registerRows(groupKey, null);
  }, [registerRows, groupKey, getRowAt, total]);
  return (
    <RowDragGroupKeyContext.Provider value={groupKey}>
      {children}
    </RowDragGroupKeyContext.Provider>
  );
}

// ---------------------------------------------------------------------------
// Slot geometry
// ---------------------------------------------------------------------------

/**
 * The slot the ghost's centre falls in among a group's rendered rows: before
 * the first rendered row whose centre lies below it, else after the last.
 * The virtualizer positions rows absolutely, so they are ordered by their
 * group index rather than trusted in DOM order. In the row's own group the
 * slot either side of the row is its own place.
 */
function resolveSlot<TData>(
  scope: HTMLElement,
  groupKey: string,
  rows: GroupRowsApi<TData>,
  active: ActiveRowDrag<TData>,
  centerY: number,
): DropSlot<TData> {
  const scopeRect = scope.getBoundingClientRect();
  const rendered = Array.from(scope.querySelectorAll<HTMLElement>(ROW_SELECTOR))
    .map((el) => ({ el, index: Number(el.dataset.rowIndex) }))
    .filter((r) => Number.isFinite(r.index))
    .sort((a, b) => a.index - b.index);

  // No rows on screen (a group still loading, or collapsed): the slot is the
  // group's head, drawn along the scope's bottom edge.
  let index = 0;
  let lineY = scopeRect.height;
  for (const { el, index: rowIndex } of rendered) {
    const rect = el.getBoundingClientRect();
    if (centerY < rect.top + rect.height / 2) {
      index = rowIndex;
      lineY = rect.top - scopeRect.top;
      break;
    }
    index = rowIndex + 1;
    lineY = rect.bottom - scopeRect.top;
  }

  const own =
    groupKey === active.groupKey &&
    active.index !== null &&
    (index === active.index || index === active.index + 1);
  const upper = index > 0 ? rows.getRowAt(index - 1) : undefined;
  const lower = rows.getRowAt(index);

  return {
    groupKey,
    index,
    upper,
    lower,
    // A slot inside the group's count with no row loaded beneath it has
    // nothing to midpoint against: the row appends past everything.
    atUnloadedTail: lower === undefined && index < rows.total,
    own,
    lineY,
  };
}

// ---------------------------------------------------------------------------
// Host — the DndContext around the groups
// ---------------------------------------------------------------------------

export function DataTableRowDragHost<TData>({
  config,
  gutterPx,
  children,
}: {
  config: DataTableRowDragConfig<TData>;
  gutterPx: number;
  children: React.ReactNode;
}) {
  const [active, setActive] = React.useState<ActiveRowDrag<TData> | null>(
    null,
  );
  const [slot, setSlot] = React.useState<DropSlot<TData> | null>(null);
  // Refs mirror the state for the drag handlers, which read the latest
  // values without re-subscribing to the DndContext on every move.
  const activeRef = React.useRef<ActiveRowDrag<TData> | null>(null);
  const slotRef = React.useRef<DropSlot<TData> | null>(null);
  // The drop settle (lib/drop-settle.ts): the ghost plate is cloned at release
  // and flown onto the row where the drop landed, carrying the pointer's
  // release velocity. The lifted node is the grip, so dnd-kit's own drop
  // animation would land on the grip; the row is the thing that moved.
  const overlayRef = React.useRef<HTMLDivElement | null>(null);
  const velocityRef = React.useRef(createVelocityTracker());
  const cancelSettleRef = React.useRef<() => void>(() => {});
  const settleTo = React.useCallback((rowId: string | undefined, expectMove: boolean) => {
    const overlay = overlayRef.current;
    if (!overlay || rowId === undefined) return;
    cancelSettleRef.current();
    const id = cssEscape(rowId);
    cancelSettleRef.current = settleOverlay({
      overlay,
      expectMove,
      velocity: velocityRef.current.velocity(),
      findTarget: () =>
        document.querySelector<HTMLElement>(`tr[data-row-id="${id}"][data-row-index]`),
    });
  }, []);
  React.useEffect(() => () => cancelSettleRef.current(), []);
  const scopesRef = React.useRef(new Map<string, HTMLElement>());
  const rowsRef = React.useRef(new Map<string, GroupRowsApi<TData>>());

  // Capability-based sensors: a mouse drags after a short travel, a finger
  // after a hold. Never gated on viewport.
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: DND_TOUCH_ACTIVATION }),
  );

  const { onDrop, getSortValue } = config;
  const placing = config.reorder ?? getSortValue !== undefined;

  // The pointer reads as a closed hand for the whole drag. The ghost takes no
  // pointer events, so the cursor would otherwise fall through to whatever
  // row or band sits under it; the document owns it until the drop.
  React.useEffect(() => {
    if (!active) return;
    const previous = document.body.style.cursor;
    document.body.style.cursor = "grabbing";
    return () => {
      document.body.style.cursor = previous;
    };
  }, [active]);

  const registerScope = React.useCallback(
    (groupKey: string, element: HTMLElement | null) => {
      if (element) scopesRef.current.set(groupKey, element);
      else scopesRef.current.delete(groupKey);
    },
    [],
  );
  const registerRows = React.useCallback(
    (groupKey: string, api: GroupRowsApi<TData> | null) => {
      if (api) rowsRef.current.set(groupKey, api);
      else rowsRef.current.delete(groupKey);
    },
    [],
  );

  const setSlotState = React.useCallback((next: DropSlot<TData> | null) => {
    if (next === null && slotRef.current === null) return;
    slotRef.current = next;
    setSlot(next);
  }, []);

  const setActiveState = React.useCallback(
    (next: ActiveRowDrag<TData> | null) => {
      activeRef.current = next;
      setActive(next);
    },
    [],
  );

  const handleDragStart = React.useCallback(
    (event: DragStartEvent) => {
      velocityRef.current.reset();
      const data = event.active.data.current as RowDragData<TData>;
      const source = data.getRowElement();
      const index = Number(source?.dataset.rowIndex);
      setActiveState({
        ...data,
        ghost: source ? cloneRow(source) : null,
        index: Number.isFinite(index) ? index : null,
      });
    },
    [setActiveState],
  );

  const handleDragMove = React.useCallback(
    (event: DragMoveEvent) => {
      const current = activeRef.current;
      const target = event.over?.data.current as GroupDropData | undefined;
      const scope = target ? scopesRef.current.get(target.groupKey) : undefined;
      const rows = target ? rowsRef.current.get(target.groupKey) : undefined;
      const rect = event.active.rect.current.translated;
      if (rect) velocityRef.current.sample({ x: rect.left, y: rect.top });
      if (!placing || !current || !target || !scope || !rows || !rect) {
        setSlotState(null);
        return;
      }
      setSlotState(
        resolveSlot(scope, target.groupKey, rows, current, rect.top + rect.height / 2),
      );
    },
    [placing, setSlotState],
  );

  const handleDragEnd = React.useCallback(
    (event: DragEndEvent) => {
      const landed = slotRef.current;
      const source = event.active.data.current as RowDragData<TData>;
      const target = event.over?.data.current as GroupDropData | undefined;
      const slot = target && landed?.groupKey === target.groupKey ? landed : null;
      // A drop that changes nothing (no target, or the row's own slot) lands
      // straight back on the row; a committed one waits for the row to move.
      const commits = target !== undefined && !slot?.own;
      settleTo(source.rowId, commits);
      setActiveState(null);
      setSlotState(null);
      if (!target || slot?.own) return;
      const upperSortValue =
        slot?.upper && getSortValue ? getSortValue(slot.upper.original) : undefined;
      const lowerSortValue =
        slot?.lower && getSortValue ? getSortValue(slot.lower.original) : undefined;
      const atUnloadedTail = slot?.atUnloadedTail ?? false;
      const result: DataTableRowDropResult<TData> = {
        row: source.row,
        fromGroupKey: source.groupKey,
        toGroupKey: target.groupKey,
        position: atUnloadedTail
          ? Number.MAX_SAFE_INTEGER
          : calculateSortOrder(upperSortValue, lowerSortValue),
        upperNeighborId: slot?.upper?.id,
        lowerNeighborId: slot?.lower?.id,
        upperSortValue,
        lowerSortValue,
        atUnloadedTail,
      };
      onDrop(result);
    },
    [onDrop, getSortValue, setActiveState, setSlotState, settleTo],
  );

  const handleDragCancel = React.useCallback(() => {
    settleTo(activeRef.current?.rowId, false);
    setActiveState(null);
    setSlotState(null);
  }, [setActiveState, setSlotState, settleTo]);

  const value = React.useMemo<RowDragContextValue<TData>>(
    () => ({
      canDrag: config.canDrag,
      canDrop: config.canDrop,
      gutterPx,
      active,
      registerScope,
      registerRows,
    }),
    [config.canDrag, config.canDrop, gutterPx, active, registerScope, registerRows],
  );

  return (
    <DndContext
      sensors={sensors}
      // Groups are stacked, full-width bands: the group under the pointer is
      // the target, and a pointer between groups (the gap) targets nothing.
      collisionDetection={pointerWithin}
      // The virtualizer seats every row by a `translateY`, and the lifted
      // row wears no drag transform of its own (the overlay carries the
      // picture). dnd-kit's stock draggable measure strips the node's
      // transform, which would seat the ghost and the slot one row's travel
      // above the pointer for every row but a group's first.
      measuring={ROW_MEASURING}
      onDragStart={handleDragStart}
      onDragMove={handleDragMove}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      <RowDragContext.Provider value={value as RowDragContextValue<unknown>}>
        <DropSlotContext.Provider value={slot as DropSlot<unknown> | null}>
          {children}
        </DropSlotContext.Provider>
        <DragOverlay dropAnimation={null}>
          {active?.ghost ? <RowGhostPlate ghost={active.ghost} overlayRef={overlayRef} /> : null}
        </DragOverlay>
      </RowDragContext.Provider>
    </DndContext>
  );
}

// ---------------------------------------------------------------------------
// Grip — the row's drag source, in the selection lane's gutter
// ---------------------------------------------------------------------------

/**
 * Renders nothing outside a `rowDrag` table or for a row `canDrag` refuses.
 * Hooks stay unconditional above the bail-out so the cell's hook order never
 * changes between rows.
 */
export function DataTableRowDragGrip<TData>({ row }: { row: Row<TData> }) {
  const rowDrag = useRowDrag<TData>();
  const groupKey = React.useContext(RowDragGroupKeyContext);
  const draggable = rowDrag !== null && (rowDrag.canDrag?.(row.original) ?? true);

  // dnd-kit stamps `role="button"` and `tabIndex=0` on the source. The grip
  // keeps neither: it is a pointer handle (no KeyboardSensor drives it), and a
  // focusable grip would hold focus after the drop and pin its own
  // hover-reveal open through `focus-within` on the row it just left.
  const gripRef = React.useRef<HTMLElement | null>(null);
  // dnd-kit's node is the ROW, not the grip: the row is what the ghost is a
  // clone of and what the finger holds on touch, and the grip sits at the
  // row's top-left corner, so the overlay's origin is the same either way.
  const rowRef = React.useRef<HTMLElement | null>(null);
  const {
    attributes: { role: _role, tabIndex: _tabIndex, ...attributes },
    listeners,
    setNodeRef,
    isDragging,
  } = useDraggable({
    id: rowDragId(groupKey, row.id),
    data: {
      row: row.original,
      rowId: row.id,
      groupKey,
      getRowElement: () => rowRef.current,
    } satisfies RowDragData<TData>,
    disabled: !draggable,
  });
  const setRef = React.useCallback(
    (node: HTMLElement | null) => {
      gripRef.current = node;
      const row = node?.closest("tr") ?? node;
      rowRef.current = row;
      setNodeRef(row);
    },
    [setNodeRef],
  );

  // Touch has no grip (see `pointer-coarse:hidden` below): the ROW is the
  // touch drag source, held anywhere on it — the board's grammar, a finger's
  // 400ms hold lifts (`DND_TOUCH_ACTIVATION`), a swipe scrolls. Bound as a
  // native CAPTURE listener on the row, ahead of any handler inside it that
  // stops `touchstart` from bubbling (a cell's context-menu trigger does),
  // and never from a field a finger is placing a caret in. dnd-kit's touch
  // activator reads only `nativeEvent` from the event it is handed.
  const touchActivator = listeners?.onTouchStart as
    | ((event: { nativeEvent: TouchEvent }) => void)
    | undefined;
  React.useEffect(() => {
    const row = rowRef.current;
    if (!draggable || !row || !touchActivator) return;
    const onTouchStart = (event: TouchEvent) => {
      if (event.touches.length !== 1) return;
      // A touch lands on an element: the one under the finger.
      const target = event.target as Element;
      if (target.closest("input, textarea, [contenteditable=true]")) return;
      touchActivator({ nativeEvent: event });
    };
    row.addEventListener("touchstart", onTouchStart, { capture: true, passive: true });
    return () => row.removeEventListener("touchstart", onTouchStart, { capture: true });
  }, [draggable, touchActivator]);

  if (!draggable) return null;

  const width = Math.max(GRIP_MIN_WIDTH, rowDrag.gutterPx);

  return (
    <Box
      ref={setRef}
      position="absolute"
      top={0}
      bottom={0}
      left={0}
      width={width}
      direction="row"
      align="center"
      justify="center"
      cursor={isDragging ? "grabbing" : "grab"}
      // Hover-revealed chrome on the row scope — the MOUSE's handle. A coarse
      // pointer has no hover and no room in the lane for one, so the grip is
      // absent there and the row itself is the touch drag source (the native
      // `touchstart` listener above).
      hoverReveal="row"
      className="pointer-coarse:hidden"
      // A pointer handle with no role and no tab stop (see above), so it
      // takes no name either: a name on a plain div is not allowed, and it
      // leaked into the row's name ("Drag to move Select row").
      aria-hidden
      // The grip claims its press: the row must not open under it.
      data-row-click-stop=""
      // Marks the row in flight so it dims under its lifted clone.
      data-row-drag-source={isDragging ? "" : undefined}
      {...DND_DRAGGABLE_PROPS}
      {...(attributes as Record<string, unknown>)}
      {...(listeners as Record<string, unknown>)}
    >
      <Icon name="grip-vertical" size="sm" color="muted" />
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Drop scope — the group as a target
// ---------------------------------------------------------------------------

/**
 * The group scope box of a `rowDrag` table: a droppable named by the group
 * key, marked `data-drop-over` while a row from ANOTHER group hovers it, and
 * the frame the insertion line is placed in. The band and the rows style
 * themselves from that attribute.
 */
export function DataTableRowDropScopeBox({
  groupKey,
  style,
  children,
}: {
  groupKey: string;
  style?: React.CSSProperties;
  children: React.ReactNode;
}) {
  const rowDrag = useRowDrag();
  const slot = React.useContext(DropSlotContext);
  const { setNodeRef, isOver } = useDroppable({
    id: groupDropId(groupKey),
    data: { groupKey } satisfies GroupDropData,
    disabled: !(rowDrag?.canDrop?.(groupKey) ?? true),
  });
  const registerScope = rowDrag?.registerScope;
  const setRef = React.useCallback(
    (node: HTMLElement | null) => {
      setNodeRef(node);
      registerScope?.(groupKey, node);
    },
    [setNodeRef, registerScope, groupKey],
  );
  const dropOver =
    isOver && rowDrag?.active != null && rowDrag.active.groupKey !== groupKey;
  const lineY = slot?.groupKey === groupKey ? slot.lineY : null;

  return (
    <Box
      ref={setRef}
      group="tgroup"
      position="relative"
      style={style}
      data-drop-over={dropOver ? "" : undefined}
    >
      {children}
      {lineY !== null ? (
        // The insertion line: a brand hairline on the boundary between the
        // two rows the drop lands between. Never a hit target.
        <Box
          position="absolute"
          left={0}
          right={0}
          height={2}
          background="primary"
          rounded="full"
          className="pointer-events-none z-30"
          style={{ top: lineY - 1 }}
          aria-hidden="true"
        />
      ) : null}
    </Box>
  );
}
